import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useLocation } from 'react-router-dom';
import MindUpAdminMobileDetailDiagnosisHeader from './MindUpAdminMobileDetailDiagnosisHeader';
import { formatDateTime } from 'utils/calculateTime';
import { Body3Medium, Body3SemiBold } from 'styles/Global/typography';
import IcCalendarPointColorBlue from 'assets/Icons/Calendar/IcCalendarPointColorBlue';
import { BorderColor, PrimaryColor, TextColor } from 'styles/Global/colorSemantic';
import BasicButton from 'components/Buttons/BasicButton';
import { getRelationText } from 'utils/getTypeText';
import { ColumnDivider } from 'styles/Global/commonStyles';
import { changeMobileForm } from 'utils/changeMobileForm';
import { ProfileCardView } from 'components/ProfileCard/ProfileCardView';
import ProcessCheckBar from 'components/Hospital/ProcessCheckBar';
import { IDDiagnosis, IDDiagnosisNull } from 'models/DCK/basicInterface';
import ConfirmDialogueListLabelHorizonLeft from 'components/DesignSystem/MobileConfirmDialogue/ConfirmDialogueListLabelHorizonLeft';
import useDiagnosisChangeStatusMutation from 'api/Mutations/Diagnosis/useDiagnosisChangeStatusMutation';
import useToggle from 'hooks/CustomHooks/useToggle';
import { AssessmentStatus, DiagnosisStatus } from 'utils/statusCode';
import _ from 'lodash';
import { MobileAssessmentButton } from 'pages/MindUp/Assessments/Components/AssessmentButton';
import { editReportTags } from 'pages/DCK/ViewDetailDiagnosisPage/utils';
import { useGetResultByDiagnosisIdQuery } from 'api/Queries/Assessment/useGetResultByDiagnosisIdQuery';
import { useLoadingStore } from 'store/useLoadingStore';
import { getDiagnosisStatus } from 'pages/DCK/utils';
import IcCalendarPointColorPurple from 'assets/Icons/Calendar/IcCalendarPointColorPurple';
import { useDiagnosisFindByIdQuery } from 'api/Queries/Diagnosis/useDiagnosisFindByIdQuery';
import { useSnackBarStore } from 'store/useToastStore';
import IcCalendarRed from 'assets/Icons/Calendar/IcCalendarRed';
import { MobileAlertDialogPCInfo } from 'components/DesignSystem/MobileAlertDialog/MobileAlertDialog';
import IcCalendarPointColorLightGray from 'assets/Icons/Calendar/IcCalendarPointColorLightGray';

const MindUpAdminMobileDetailDiagnosisPage = () => {
    const { state } = useLocation();
    const resultSectionRef = useRef<HTMLDivElement>(null);

    const [diagnosisInfo, setDiagnosisInfo] = useState<IDDiagnosisNull>(state);

    const [onConfirmModal, setOnConfirmModal] = useToggle();
    const [onInfoModal, setOnInfoModal] = useToggle();
    const [onNoShowModal, setOnNoShowModal] = useToggle();

    const { setLoading } = useLoadingStore();
    const { showSnackBar, hideSnackBar } = useSnackBarStore();

    const { mutate: diagnosisChangeStatusMutation } = useDiagnosisChangeStatusMutation();
    const { data: diagnosisData, refetch: refetchDiagnosisData } = useDiagnosisFindByIdQuery(state._id);
    const { data: resultDiagnosis, refetch: refetchResultDiagnosis } = useGetResultByDiagnosisIdQuery(diagnosisInfo._id!);

    const info = {
        학력학기: diagnosisInfo.gradeName,
        주양육자: (
            <>
                {getRelationText(diagnosisInfo.relationType!)}
                <ColumnDivider $height='12px' />
                {diagnosisInfo.parentName}
            </>
        ),
        연락처: changeMobileForm(diagnosisInfo.patientMobile!),
        담당자: diagnosisInfo.doctorName,
        검사동기: diagnosisInfo.counselReason,
    };

    // snackbar setting
    useEffect(() => {
        const toastShown = localStorage.getItem('snackbar');

        if (toastShown === 'true') {
            if (getDiagnosisStatus(state) === 'counselComplete') {
                showSnackBar('상담이 완료 처리 되었습니다!', 'success');
            } else {
                showSnackBar('상담 확정이 완료되었습니다!', 'success', '검사 결과보기', moveFocus);
            }
        }
    }, [state]);

    const moveFocus = () => {
        resultSectionRef.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
        });
        hideSnackBar();
    };

    useEffect(() => {
        if (diagnosisData && diagnosisData._id) {
            let newCounselDateTimeCount = 0;

            const addTagData = editReportTags(diagnosisData.assessmentList);

            // 상담 일시 수정 count
            diagnosisData.noShowInfo?.forEach((item) => {
                item.newCounselDateTime && newCounselDateTimeCount++;
            });

            const addTagInState = {
                ...diagnosisData,
                assessmentList: addTagData,
                _id: diagnosisData._id,
                noShowDisabled: diagnosisData.noShowInfo?.length !== 2 && diagnosisData.noShowInfo?.length === newCounselDateTimeCount ? false : true,
            };

            setDiagnosisInfo(addTagInState);
        }
    }, [diagnosisData]);

    const processCheckBarClick = (item: IDDiagnosisNull) => {
        // 새로 상담 일시 잡기
        // if (item.noShowDisabled && onChangeDiagnosisDateModal) {
        //         onChangeDiagnosisDateModal(item);
        //     }
        //    상담 완료
        if (getDiagnosisStatus(item) === 'reschedule') {
            setOnInfoModal();
        } else {
            setOnConfirmModal();
        }
    };

    const clickCancelButton = () => {
        setOnConfirmModal();
    };

    // 상담 완료 함수
    const handleCounselComplete = async () => {
        if (!diagnosisInfo._id) {
            return;
        }

        const changeStatusRqData = {
            _id: diagnosisInfo._id,
            status: DiagnosisStatus.counselComplete,
        };

        diagnosisChangeStatusMutation(changeStatusRqData, {
            onSuccess: () => {
                localStorage.setItem('snackbar', 'true');
                clickCancelButton();
                refetchDiagnosisData();
                showSnackBar('상담이 완료 처리 되었습니다!', 'success');
            },
            onError: (error) => {
                console.error('상담 완료 처리 실패', error);
            },
        });
    };

    const viewOzUrl = async (assessmentCode: string) => {
        setLoading(true);

        const foundAssessment = _.filter(resultDiagnosis, (e) => e.ozJsonInfoData.PSY_ITEM_ID === assessmentCode);

        if (foundAssessment[0]?.ozUrl) {
            // window.location.href = foundAssessment[0].ozUrl;
            window.open(foundAssessment[0].ozUrl, '_blank');
            // 로딩 상태 즉시 해제
            setLoading(false);
        } else {
            setLoading(false); // 에러 시 로딩 해제
            alert('결과를 불러올 수 없습니다.');
        }
    };

    return (
        <Layout>
            <MindUpAdminMobileDetailDiagnosisHeader diagnosis={diagnosisInfo} />
            {onNoShowModal && (
                <MobileAlertDialogPCInfo
                    firstTitle='현재는 PC버전에서만'
                    secondTitle='노쇼 처리가 가능합니다.'
                    firstContent='불편을 드려 죄송합니다.'
                    secondContent='아래의 상담사 사이트를 PC로 접속하여 노쇼 처리와 상담 일정 변경을 부탁드립니다.'
                    info='http://tlcdoctors.co.kr'
                    button='확인'
                    clickButton={setOnNoShowModal}
                />
            )}
            {onInfoModal && (
                <MobileAlertDialogPCInfo
                    firstTitle='현재는 PC버전에서만'
                    secondTitle='상담 일시 변경이 가능합니다.'
                    firstContent='불편을 드려 죄송합니다.'
                    secondContent='아래의 상담사 사이트를 PC로 접속하여 노쇼 처리된 상담 일정을 변경 부탁드립니다.'
                    info='http://tlcdoctors.co.kr'
                    button='확인'
                    clickButton={setOnInfoModal}
                />
            )}
            {onConfirmModal && (
                <ConfirmDialogueListLabelHorizonLeft
                    title='상담을 완료하셨나요?'
                    content='상담을 끝내셨다면, 완료 처리해주세요.'
                    diagnosisInfo={diagnosisInfo}
                    firstButton='취소'
                    secondButton='상담 완료'
                    clickFirstButton={clickCancelButton}
                    clickSecondButton={handleCounselComplete}
                />
            )}
            <DateContainer>
                <DateWrapper>
                    {getDiagnosisStatus(diagnosisInfo) === 'reschedule' ? (
                        <IcCalendarRed />
                    ) : getDiagnosisStatus(diagnosisInfo) === 'counselComplete' ? (
                        <IcCalendarPointColorPurple />
                    ) : getDiagnosisStatus(diagnosisInfo) === 'noshow' ? (
                        <IcCalendarPointColorLightGray />
                    ) : (
                        <IcCalendarPointColorBlue />
                    )}
                    {/* <Date $state={getDiagnosisStatus(diagnosisInfo) === 'reschedule' || getDiagnosisStatus(diagnosisInfo) === 'noshow'}> */}
                    <Date $state={getDiagnosisStatus(diagnosisInfo)}>{formatDateTime(diagnosisInfo.diagnosisDateTime!)}</Date>
                    <Counseling>상담</Counseling>
                </DateWrapper>
                {getDiagnosisStatus(diagnosisInfo) !== 'counselComplete' && getDiagnosisStatus(diagnosisInfo) !== 'noshow' && (
                    <BasicButton $type='fillLightRed' $height='36px' $width='61px' $fontSize='13px' onClick={setOnNoShowModal} disabled={getDiagnosisStatus(diagnosisInfo) === 'reschedule'}>
                        노쇼처리
                    </BasicButton>
                )}
            </DateContainer>
            <ProfileAreaContainer>
                <ProfileCardView data={diagnosisInfo!} info={info} />
                {getDiagnosisStatus(diagnosisInfo) !== 'counselComplete' && getDiagnosisStatus(diagnosisInfo) !== 'noshow' && (
                    <ProcessCheckBar diagnosisInfo={diagnosisInfo} onCLickInMindUpMobileAdmin={processCheckBarClick} $barType='mobileButton' hover='none' />
                )}
            </ProfileAreaContainer>
            <DivideLine />
            <AssessmentResultListContainer ref={resultSectionRef}>
                <AssessmentResultTitle>
                    검사 종합 결과<span style={{ color: PrimaryColor.color_primary_normal }}>{diagnosisInfo.assessmentList!.length}</span>
                </AssessmentResultTitle>
                <AssessmentListContainer>
                    {_.map(diagnosisInfo.assessmentList, (assessment, index) => {
                        return (
                            <MobileAssessmentButton
                                $title={assessment.group}
                                $subTitle={''}
                                $desc={_.first(_.split(assessment.assessmentName, '(')) ?? ''}
                                $label={assessment.tags[0]}
                                $type={assessment.tags[0]}
                                $bedgeType='strong'
                                $number={Number(index) + 1}
                                $assessmentStatus={AssessmentStatus.standBy}
                                $subName={assessment.subName}
                                isDisable={false}
                                $onClick={() => viewOzUrl(assessment.assessmentCode)}
                            />
                        );
                    })}
                </AssessmentListContainer>
            </AssessmentResultListContainer>
        </Layout>
    );
};

export default MindUpAdminMobileDetailDiagnosisPage;

const Layout = styled.section`
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    min-height: 100vh;
    height: auto;
    padding-top: 56px;
`;

const ProfileAreaContainer = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    gap: 16px;
    padding: 0 16px 32px 16px;
`;

const DateContainer = styled.div`
    display: flex;
    padding: 12px 16px 16px;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    height: 60px;
    gap: 4px;
`;

const DateWrapper = styled.div`
    display: flex;
    align-items: center;
    height: 100%;
    width: 263px;
`;

const Date = styled(Body3SemiBold)<{ $state: string }>`
    margin-left: 6px;
    ${(props) =>
        (props.$state === 'reschedule' || props.$state === 'noshow') &&
        `text-decoration: line-through ${TextColor.color_text_light};
        -webkit-text-decoration: line-through;
        text-decoration-line: line-through;
        text-decoration-style: solid;
        `};
    color: ${(props) => (props.$state === 'reschedule' ? TextColor.color_text_light : props.$state === 'noshow' ? TextColor.color_text_weak : TextColor.color_text_normal)};
`;

const Counseling = styled(Body3Medium)`
    color: ${TextColor.color_text_light};
    margin-left: 2px;
`;

const AssessmentResultListContainer = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    gap: 10px;
    padding: 32px 16px 56px 16px;
`;

const AssessmentListContainer = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    gap: 12px;
`;

const AssessmentResultTitle = styled(Body3SemiBold)`
    display: flex;
    align-items: center;
    width: 100%;
    gap: 4px;
`;

const DivideLine = styled.div`
    width: 100%;
    height: 12px;
    background-color: ${BorderColor.color_border_weak};
    flex-shrink: 0;
`;
